import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { USER_API, toastMsg } from "config";
import { budgetKeys } from "queries";
import { getToastMessageForError, handleToast, ServerError } from "utils";
import { isAxiosError } from "axios";
import { BudgetI, BudgetVersions } from "components/dashboard/body/projects/panels/budget/types";
import { getBudgetTotalEstimate } from "components/dashboard/body/projects/panels/budget/BudgetItemUtils";
import { ComparisonScenario } from "components/dashboard/body/projects/panels/budget/BudgetScenarioCompareModal/BudgetScenarioCompareModal.types";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { useAxios } from "../useAxios";
import { useBudgetApi, useBudgetMutations } from ".";
import { useBudgetVersions } from "./useBudgetVersions";
import { getLiveBudgetVersionId } from "./useBudgetList";
import { useProjectAuth } from "../project/useProjectAuth";

const budgetTotal = (
  budget: Pick<
    BudgetI,
    "categories" | "contingency_percentage" | "contingency_locked" | "contingency_actual"
  >
): number =>
  getBudgetTotalEstimate(budget.categories, {
    contingency_percentage: budget.contingency_percentage,
    locked: budget.contingency_locked,
    actual_amount: budget.contingency_actual,
  });

const newestFirst = (a: BudgetVersions, b: BudgetVersions): number => {
  const aTime = a.created_at ? Date.parse(a.created_at) : 0;
  const bTime = b.created_at ? Date.parse(b.created_at) : 0;
  if (bTime !== aTime) return bTime - aTime;
  return b.budget_id - a.budget_id;
};

const scenarioFromVersion = (
  version: BudgetVersions,
  total: number,
  liveVersionId: number | undefined
): ComparisonScenario => ({
  id: `version-${version.budget_id}`,
  budgetId: version.budget_id,
  label: version.version_name?.trim() || `Version ${version.budget_id}`,
  total,
  kind: "saved",
  createdAt: version.created_at,
  isPrimary: version.budget_id === liveVersionId,
});

interface UseBudgetScenarioComparisonParams {
  open: boolean;
}

export const useBudgetScenarioComparison = ({ open }: UseBudgetScenarioComparisonParams) => {
  const { projectId, documentId } = useProjectIdentifiers();
  const { budgetId, budgetData } = useBudgetApi(open);
  const { isViewer } = useProjectAuth(projectId);
  const { updateBudgetVersion, deleteBudgetVersion, restoreBudgetVersion, refreshBudgetVersions } =
    useBudgetMutations({
      projectId: projectId ?? "",
      documentId: documentId ?? "",
      budgetId: budgetId ?? "",
    });
  const queryClient = useQueryClient();
  const versionsQuery = useBudgetVersions({ projectId, documentId, enabled: open });
  const axios = useAxios({ allResponse: false });

  const [scenarioName, setScenarioName] = useState("");
  const [pinnedVersions, setPinnedVersions] = useState<ComparisonScenario[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [loadingVersionId, setLoadingVersionId] = useState<number | null>(null);
  const [saveErrorCode, setSaveErrorCode] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setScenarioName("");
    setPinnedVersions([]);
    setIsSaving(false);
    setLoadingVersionId(null);
    setSaveErrorCode(null);
  }, [open]);

  // The version the budget pages load and edit, by the same rule, even when none is primary.
  const liveVersionId = useMemo(
    () => getLiveBudgetVersionId(versionsQuery.data ?? []),
    [versionsQuery.data]
  );

  const versionQuota = useMemo(() => {
    const versions = versionsQuery.data ?? [];
    const fromRow = versions.find((version) => version.quota)?.quota;
    if (fromRow) return fromRow;
    return null;
  }, [versionsQuery.data]);

  const baselineTotal = useMemo(
    () => (budgetData?.categories ? budgetTotal(budgetData) : 0),
    [budgetData]
  );

  const scenarios = useMemo<ComparisonScenario[]>(() => {
    if (!budgetData?.categories) return pinnedVersions;
    const baseline: ComparisonScenario = {
      id: "baseline",
      label: "Current budget",
      total: baselineTotal,
      kind: "baseline",
    };
    return [baseline, ...pinnedVersions];
  }, [budgetData, baselineTotal, pinnedVersions]);

  const loadedVersionIds = useMemo(
    () => new Set(pinnedVersions.map((scenario) => scenario.budgetId)),
    [pinnedVersions]
  );

  const trimmedName = scenarioName.trim();

  const duplicateName = useMemo(
    () =>
      Boolean(trimmedName) &&
      (versionsQuery.data ?? []).some(
        (version) => (version.version_name || "").trim().toLowerCase() === trimmedName.toLowerCase()
      ),
    [trimmedName, versionsQuery.data]
  );

  const canSave =
    Boolean(budgetId) && Boolean(trimmedName) && !duplicateName && !isSaving && !isViewer;

  const removeScenario = useCallback((versionBudgetId: number) => {
    setPinnedVersions((current) =>
      current.filter((scenario) => scenario.budgetId !== versionBudgetId)
    );
  }, []);

  const clearScenarios = useCallback(() => setPinnedVersions([]), []);

  const saveScenario = useCallback(async () => {
    const name = trimmedName;
    if (!budgetId || !name || duplicateName || isSaving || isViewer) return;
    setIsSaving(true);
    setSaveErrorCode(null);
    try {
      await axios.post(
        USER_API.saveBudgetVersion(String(projectId), String(documentId), budgetId),
        { version_name: name }
      );
      setScenarioName("");
      // Reload the versions and the edited budget together, then pin the new scenario.
      await refreshBudgetVersions();
      const refreshed =
        queryClient.getQueryData<BudgetVersions[]>(budgetKeys.versions(projectId, documentId)) ??
        [];
      const match = refreshed
        .filter((version) => (version.version_name || "").trim() === name)
        .sort(newestFirst)[0];
      if (match) {
        setPinnedVersions((current) =>
          current.some((scenario) => scenario.budgetId === match.budget_id)
            ? current
            : [...current, scenarioFromVersion(match, baselineTotal, liveVersionId)]
        );
      }
      handleToast(`Saved “${name}” as a scenario`, "success");
    } catch (error) {
      // A failed save can still have changed versions on the server.
      void refreshBudgetVersions();
      const serverError = isAxiosError(error) ? ServerError.fromAxiosError(error) : null;
      setSaveErrorCode(serverError?.code ?? null);
      handleToast(
        serverError ? getToastMessageForError(serverError) : toastMsg.genericServerErr,
        "error"
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    trimmedName,
    budgetId,
    duplicateName,
    isSaving,
    isViewer,
    axios,
    projectId,
    documentId,
    refreshBudgetVersions,
    queryClient,
    baselineTotal,
    liveVersionId,
  ]);

  const toggleVersion = useCallback(
    async (version: BudgetVersions) => {
      if (loadingVersionId !== null) return;
      if (loadedVersionIds.has(version.budget_id)) {
        removeScenario(version.budget_id);
        return;
      }
      setLoadingVersionId(version.budget_id);
      try {
        const response = await axios.get<BudgetI>(
          USER_API.getBudget(String(projectId), String(documentId), String(version.budget_id))
        );
        const budget = ((response as { data?: BudgetI }).data ?? response) as BudgetI;
        const total = budgetTotal(budget);
        setPinnedVersions((current) =>
          current.some((scenario) => scenario.budgetId === version.budget_id)
            ? current
            : [...current, scenarioFromVersion(version, total, liveVersionId)]
        );
      } catch {
        handleToast(toastMsg.genericServerErr, "error");
      } finally {
        setLoadingVersionId(null);
      }
    },
    [
      loadingVersionId,
      loadedVersionIds,
      removeScenario,
      axios,
      projectId,
      documentId,
      liveVersionId,
    ]
  );

  const isLive = useCallback(
    (version: BudgetVersions) => version.budget_id === liveVersionId,
    [liveVersionId]
  );

  const makeVersionLive = useCallback(
    (version: BudgetVersions) => {
      if (isViewer || isLive(version)) return;
      updateBudgetVersion.mutate({
        budgetId: String(version.budget_id),
        update: { primary: true },
      });
    },
    [isViewer, isLive, updateBudgetVersion]
  );

  const deleteVersion = useCallback(
    (version: BudgetVersions) => {
      if (isViewer || isLive(version)) return;
      deleteBudgetVersion.mutate(String(version.budget_id));
    },
    [deleteBudgetVersion, isLive, isViewer]
  );

  const restoreVersion = useCallback(
    (version: BudgetVersions) => {
      if (isViewer || isLive(version)) return;
      restoreBudgetVersion.mutate(String(version.budget_id));
    },
    [isViewer, isLive, restoreBudgetVersion]
  );

  return {
    scenarioName,
    setScenarioName,
    trimmedName,
    duplicateName,
    canSave,
    isSaving,
    saveErrorCode,
    loadingVersionId,
    loadedVersionIds,
    scenarios,
    hasBudget: Boolean(budgetData?.categories),
    versionsQuery,
    liveVersionId,
    versionQuota,
    saveScenario,
    toggleVersion,
    removeScenario,
    clearScenarios,
    makeVersionLive,
    deleteVersion,
    restoreVersion,
    isViewer,
  };
};
